import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Volume2, VolumeX, Waves, Moon } from "lucide-react";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { getVoicePref } from "@/lib/voicePref";
import { isSoundEnabled } from "@/lib/chime";
import {
  startBreathAmbience, stopBreathAmbience, duckBreathAmbience,
} from "@/lib/breathAmbience";
import { scaleToTarget, totalMs, speakMs } from "@/lib/meditationVariants";

/**
 * YogaNidraMeditation — a 5-minute Yoga Nidra practice delivered as a
 * beat sequence of speak, pause, and phase steps.
 *
 * Follows the traditional Yoga Nidra flow — settling → body stillness →
 * breath awareness → rotation of consciousness (moving attention through
 * hands, feet, face, whole body) → deep rest → gentle return.
 *
 * The breathing ambience pad plays underneath the whole session; each
 * spoken sentence waits for the TTS utterance to end before advancing,
 * and pause beats hold real silence. Every audio source is torn down
 * on close/unmount via the same session-token protected readAloud
 * plumbing used by the other meditation overlays.
 */

const BEATS = [
  // ------- Settling -------
  { type: "speak", text: "Take a moment to settle into a comfortable position. Lie down if you can, or sit with full support. Let your body become still. Let your breath soften." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Gently close your eyes. Feel the weight of your body resting. Feel the ground beneath you holding everything." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Take a slow inhale… and a slow exhale." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Allow your breath to move naturally. No effort. No control. Just the gentle rise and fall." },

  // ------- Body Stillness -------
  { type: "phase", label: "Body stillness" },
  { type: "speak", text: "Let your body become completely still. Let your face soften. Let your shoulders relax. Let your belly loosen." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Feel the entire body resting in quiet awareness." },

  // ------- Breath Awareness -------
  { type: "phase", label: "Breath awareness" },
  { type: "speak", text: "Bring your attention to the breath. Notice the coolness as you inhale… and the warmth as you exhale." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Feel the breath flowing in… and flowing out… like a gentle tide." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Let the breath guide you deeper into relaxation." },

  // ------- Rotation of Consciousness -------
  { type: "phase", label: "Rotation of consciousness" },
  { type: "speak", text: "Now we'll move awareness through the body. Simply notice each part as it's named." },
  { type: "pause", ms: 5000 },
  { type: "speak", text: "Awareness in the right hand… the right thumb… index finger… middle finger… ring finger… little finger…" },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Awareness in the left hand… left thumb… index finger… middle finger… ring finger… little finger…" },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Awareness in both feet… the right foot… the left foot… the toes… the heels… the arches…" },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Awareness in the face… the jaw… the cheeks… the eyes… the forehead…" },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Awareness in the whole body… resting… softening… releasing." },
  { type: "pause", ms: 15000 },

  // ------- Deep Rest -------
  { type: "phase", label: "Deep rest" },
  { type: "speak", text: "Feel your entire body heavy and relaxed. Feel your breath slow and steady. Feel your mind quiet and spacious." },
  { type: "pause", ms: 20000 },
  { type: "speak", text: "You are resting in awareness. You are safe. You are held. You are at ease." },
  { type: "pause", ms: 20000 },

  // ------- Closing -------
  { type: "phase", label: "Closing" },
  { type: "speak", text: "Begin to deepen your breath. Gently waking the body. Soft movement in the fingers… and the toes…" },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Take one slow inhale… and one full exhale." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "When you're ready, slowly open your eyes. Carry this calm with you." },
];

function beatDurationMs(beat) {
  if (beat.type === "pause") return beat.ms || 0;
  if (beat.type === "phase") return 800;
  if (beat.type === "speak") return speakMs(beat.text);
  return 0;
}

const TOTAL_MS = BEATS.reduce((acc, b) => acc + beatDurationMs(b), 0);

// Wall-clock targets by variant.
const TARGETS = {
  beginner:      5 * 60 * 1000,
  intermediate: 10 * 60 * 1000,
  advanced:     20 * 60 * 1000,
};

export default function YogaNidraMeditation({ open, onClose, variant = "beginner" }) {
  const target = TARGETS[variant] ?? TARGETS.beginner;
  const beats = React.useMemo(() => scaleToTarget(BEATS, target, variant), [target, variant]);
  const TOTAL_MS_LOCAL = React.useMemo(() => totalMs(beats), [beats]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [narrating, setNarrating] = useState(() => canSpeak() && getVoicePref().enabled);
  const [ambience, setAmbience] = useState(() => isSoundEnabled());
  const [elapsedMs, setElapsedMs] = useState(0);
  const [currentPhase, setCurrentPhase] = useState("");
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setBeatIdx(0);
    setPaused(false);
    setElapsedMs(0);
    setCurrentPhase("");
  }, [open]);

  useEffect(() => () => {
    cancelledRef.current = true;
    stopSpeaking();
    stopBreathAmbience();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!open) { stopBreathAmbience(); return undefined; }
    if (!ambience) { stopBreathAmbience(); return undefined; }
    startBreathAmbience();
    return () => { /* handled by open==false branch */ };
  }, [open, ambience]);

  useEffect(() => {
    if (!ambience || !open) return;
    duckBreathAmbience(paused ? 0.35 : 1.0, 0.6);
  }, [paused, ambience, open]);

  useEffect(() => {
    if (!open || paused || cancelledRef.current) return undefined;
    const beat = beats[beatIdx];
    if (!beat) return undefined;
    let cleanup = () => {};

    const advance = () => {
      if (cancelledRef.current) return;
      setElapsedMs((ms) => ms + beatDurationMs(beat));
      if (beatIdx + 1 >= beats.length) return;
      setBeatIdx(beatIdx + 1);
    };

    if (beat.type === "speak") {
      if (narrating && canSpeak()) {
        speak(beat.text, { context: "meditation", onEnd: advance });
        cleanup = () => stopSpeaking();
      } else {
        timeoutRef.current = setTimeout(advance, beatDurationMs(beat));
        cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
      }
    } else if (beat.type === "pause") {
      timeoutRef.current = setTimeout(advance, beat.ms || 0);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    } else if (beat.type === "phase") {
      setCurrentPhase(beat.label);
      timeoutRef.current = setTimeout(advance, 800);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }

    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, open, paused, narrating]);

  useEffect(() => {
    if (paused) {
      stopSpeaking();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [paused]);

  useEffect(() => {
    const onPref = () => {
      const enabled = getVoicePref().enabled;
      if (!enabled) { setNarrating(false); stopSpeaking(); }
    };
    window.addEventListener("n8-voice-pref-changed", onPref);
    return () => window.removeEventListener("n8-voice-pref-changed", onPref);
  }, []);

  if (!open) return null;

  const beat = beats[beatIdx];
  const finished = beatIdx >= beats.length - 1 && !paused;
  const progress = Math.min(100, Math.round((elapsedMs / TOTAL_MS_LOCAL) * 100));
  const displayText = beat?.type === "speak" ? beat.text : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Yoga Nidra meditation"
      data-testid="yoga-nidra-overlay"
    >
      {/* Deep midnight indigo backdrop — Yoga Nidra reads as night-sky */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(10, 12, 24, 0.985)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 45%, rgba(150,160,220,0.14) 0%, rgba(60,60,110,0.05) 45%, transparent 78%)",
        }}
      />

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5" data-testid="yoga-nidra-progress-track">
        <div
          className="h-full bg-white/50 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
          data-testid="yoga-nidra-progress-bar"
        />
      </div>

      {currentPhase && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-white/60"
          data-testid="yoga-nidra-phase"
        >
          {currentPhase}
        </div>
      )}

      {/* Close — z-30 so the main stage below can't intercept the click. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close meditation"
        data-testid="yoga-nidra-close"
        className="absolute top-4 right-4 z-30 rounded-full p-2.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/15 tab-link"
      >
        <X size={18} />
      </button>

      {/* Main stage */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-8 text-center gap-10">
        <MoonGlow />

        <div className="max-w-2xl min-h-[3.5em]">
          <p
            key={beatIdx}
            className="font-display italic text-white/90 text-2xl md:text-3xl leading-relaxed animate-[fade-up_800ms_ease-out]"
            data-testid="yoga-nidra-text"
          >
            {displayText}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume meditation" : "Pause meditation"}
            data-testid="yoga-nidra-toggle"
            className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 tab-link"
            disabled={finished}
          >
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          {canSpeak() && (
            <button
              type="button"
              onClick={() => setNarrating((n) => !n)}
              aria-label={narrating ? "Mute N8's voice" : "Unmute N8's voice"}
              aria-pressed={narrating}
              data-testid="yoga-nidra-narration-toggle"
              className={`rounded-full p-2 tab-link ${
                narrating
                  ? "text-white bg-white/15 hover:bg-white/25"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
              disabled={finished}
            >
              {narrating ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAmbience((a) => !a)}
            aria-label={ambience ? "Mute breathing ambience" : "Play breathing ambience"}
            aria-pressed={ambience}
            data-testid="yoga-nidra-ambience-toggle"
            title={ambience ? "Ambience on" : "Ambience off"}
            className={`rounded-full p-2 tab-link ${
              ambience
                ? "text-white bg-white/15 hover:bg-white/25"
                : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
            disabled={finished}
          >
            <Waves size={15} />
          </button>
        </div>

        {finished && (
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60" data-testid="yoga-nidra-complete">
            Meditation complete
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * MoonGlow — a soft cool-blue moon that gently pulses. Yoga Nidra is
 * traditionally a night-time practice, so the visual leans lunar rather
 * than solar. Same footprint as the other meditation ring visuals so
 * the tab feels cohesive.
 */
function MoonGlow() {
  return (
    <div className="relative grid place-items-center h-[300px] w-[300px]" data-testid="yoga-nidra-glow">
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 260,
          height: 260,
          animation: "nidra-aura-pulse 10s ease-in-out infinite",
          background: "radial-gradient(circle, rgba(150,170,230,0.22) 0%, rgba(150,170,230,0) 68%)",
        }}
      />
      <div
        aria-hidden
        className="absolute rounded-full border border-white/35"
        style={{
          width: 200,
          height: 200,
          animation: "nidra-ring-pulse 10s ease-in-out infinite",
        }}
      />
      <Moon
        aria-hidden
        size={40}
        strokeWidth={1.4}
        className="text-white/85 relative z-10"
        style={{ animation: "nidra-moon-pulse 10s ease-in-out infinite" }}
      />
      <style>{`
        @keyframes nidra-ring-pulse {
          0%, 100% { transform: scale(0.95); opacity: 0.55; }
          50%      { transform: scale(1.06); opacity: 1;    }
        }
        @keyframes nidra-aura-pulse {
          0%, 100% { opacity: 0.5;  transform: scale(0.96); }
          50%      { opacity: 1;    transform: scale(1.08); }
        }
        @keyframes nidra-moon-pulse {
          0%, 100% { transform: scale(0.96); }
          50%      { transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}
